// @mui
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import { CardProps } from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import { useTheme, alpha } from '@mui/material/styles';
// components
import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

const COLOR_CRIMSON = '#FF6D00';
const COLOR_SURFACE = '#0A0A0A';
const COLOR_SURFACE_LIGHT = '#151515';

type Props = CardProps & {
  card: {
    subscription: string;
    price: number;
    caption: string;
    labelAction: string;
    lists: string[];
    link: string;
    yearlyPrice: number;
    yearlyLink: string;
    featured?: boolean;
    yearlyNonDiscountedPrice?: number;
  };
  index: number;
  monthly: boolean;
};

export default function PricingCard({ card, sx, monthly, ...other }: Props) {
  const { subscription, price, caption, lists, labelAction, link, yearlyPrice, yearlyLink, featured, yearlyNonDiscountedPrice } = card;

  const isFeatured = featured || subscription === 'professional';

  return (
    <Stack
      spacing={4}
      sx={{
        p: 5,
        height: '100%',
        borderRadius: '16px', // Less boxy shape
        background: COLOR_SURFACE,
        border: isFeatured
          ? `1px solid ${COLOR_CRIMSON}`
          : `1px solid ${COLOR_SURFACE_LIGHT}`,
        position: 'relative',
        transition: 'all 0.2s ease',
        '&:hover': {
          border: isFeatured ? `1px solid ${COLOR_CRIMSON}` : `1px solid ${alpha(COLOR_CRIMSON, 0.4)}`,
          transform: 'translateY(-4px)',
          boxShadow: isFeatured ? `0 0 30px ${alpha(COLOR_CRIMSON, 0.15)}` : 'none',
        },
        ...sx,
      }}
      {...other}
    >
      {/* Popular Badge */}
      {isFeatured && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            background: COLOR_CRIMSON,
            color: '#000',
            fontSize: '0.7rem',
            fontWeight: 800,
            px: 2,
            py: 0.5,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
          }}
        >
          POPULAR
        </Box>
      )}

      {/* Plan Name & Description */}
      <Stack spacing={1}>
        <Typography
          variant="h5"
          sx={{
            textTransform: 'uppercase',
            fontWeight: 800,
            letterSpacing: '0.05em',
            color: isFeatured ? COLOR_CRIMSON : '#fff',
          }}
        >
          {subscription}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: '#888',
            minHeight: 40,
          }}
        >
          {caption}
        </Typography>
      </Stack>

      {/* Price */}
      <Stack spacing={0.5}>
        {!monthly && yearlyNonDiscountedPrice && (
          <Typography
            sx={{
              textDecoration: 'line-through',
              color: '#555',
              fontSize: '0.9rem',
            }}
          >
            ${yearlyNonDiscountedPrice}/yr
          </Typography>
        )}
        <Stack direction="row" alignItems="baseline" spacing={0.5}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              fontFamily: 'monospace',
              color: '#fff',
            }}
          >
            ${monthly ? price : yearlyPrice}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#888',
            }}
          >
            / {monthly ? 'mo' : 'yr'}
          </Typography>
        </Stack>
      </Stack>

      {/* Features */}
      <Stack spacing={2} sx={{ flex: 1 }}>
        <Typography
          variant="overline"
          sx={{
            color: isFeatured ? COLOR_CRIMSON : '#666',
            letterSpacing: '0.1em',
            fontSize: '0.7rem',
          }}
        >
          CAPABILITIES INCLUDED
        </Typography>

        {lists.map((item) => (
          <Stack
            key={item}
            spacing={1.5}
            direction="row"
            alignItems="flex-start"
          >
            <Box
              sx={{
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                mt: 0.25,
              }}
            >
              <Iconify
                icon="mdi:square"
                width={10}
                sx={{ color: isFeatured ? COLOR_CRIMSON : '#555' }}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: '#CCC',
                lineHeight: 1.6,
              }}
            >
              {item}
            </Typography>
          </Stack>
        ))}
      </Stack>

      {/* CTA Button */}
      <Button
        fullWidth
        size="large"
        variant={isFeatured ? "contained" : "outlined"}
        href={monthly ? link : yearlyLink}
        target="_blank"
        sx={{
          py: 1.5,
          borderRadius: '8px',
          fontWeight: 700,
          fontSize: '0.85rem', // Slightly smaller to prevent wrapping
          whiteSpace: 'nowrap', // Ensure single line
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          boxShadow: 'none',
          background: isFeatured ? COLOR_CRIMSON : 'transparent',
          color: isFeatured ? '#000' : COLOR_CRIMSON,
          border: `1px solid ${COLOR_CRIMSON}`,
          transition: 'all 0.2s ease',
          '&:hover': {
            background: COLOR_CRIMSON,
            color: '#000',
            boxShadow: `0 0 20px ${alpha(COLOR_CRIMSON, 0.4)}`,
          },
        }}
      >
        {labelAction}
      </Button>
    </Stack>
  );
}
